/**
 * clientFacingRoutes: the ONE list of pages a client (not a member of
 * staff) lands on from a link the firm emails them.
 *
 * BM-H604 (23 Sep 2026). Vanessa: clients were being sent to the staff
 * Microsoft (M365) login from the onboarding form. "This must never
 * happen." The mechanism was the same every time: a client page called an
 * API route that sits behind the staff session seal, the backend answered
 * 401 SESSION_EXPIRED, and the global axios interceptor in config/api.js
 * did window.location.href = "/login?session_expired=1", which is the staff
 * sign-in page.
 *
 * Everything that could send a visitor to /login consults this helper
 * first: the axios interceptor, ProtectedRoute and CatchAllRedirect in
 * App.js. On a client path they show CLIENT_LINK_EXPIRED_MESSAGE instead.
 *
 * Adding a new client page? Add its path prefix here. The Jest test
 * config/__tests__/clientFacingRoutes.bmh604.test.js fails if a public
 * route in App.js that looks client-facing is missing from this list, and
 * the backend ratchet (backend/tests/test_bmh604_*) drives every API call
 * those pages make through the real sealed server.
 *
 * Deliberately NOT here:
 *   /conflict-approval/:token  is for the approving fee earner (staff).
 *                              BM-H267 sealed its routes on purpose: the
 *                              approver must be signed in to decide, so a
 *                              401 there SHOULD go to the staff login.
 *                              (PR #2937 had put it on this list; that left
 *                              an approver with an expired session stuck on
 *                              an error instead of being asked to sign in.)
 *   /login, /auth-complete     are the staff sign-in pages themselves.
 */

export const CLIENT_FACING_PATH_PREFIXES = Object.freeze([
  "/client-onboarding",     // OB form, single (?ref=&t= / ?pid=) and joint (/joint/:token)
  "/terms/",                // Terms of Business acceptance (link_ob / skip_ob)
  "/sign/",                 // CCL signing
  "/signing/",              // CCL signing (legacy link shape)
  "/loa/",                  // Letter of Authority signing (BM-H3851)
  "/portal/",               // client document portal
  "/sof/",                  // source of funds questionnaire
  "/sdlt/",                 // SDLT questionnaire
  "/ta6/",                  // property information form
  "/ta7/",                  // leasehold information form
  "/ta10/",                 // fittings and contents form
  "/bonsai-pen/client/",    // Bonsai Pen client questionnaire
  "/client-forms/",         // wills, LPA and EPR client forms
]);

export const CLIENT_LINK_EXPIRED_MESSAGE =
  "This link has expired or is no longer valid. Please contact us and we will send a new one.";

/**
 * True when `pathname` is a page a client reaches from an emailed link.
 * Matching is on whole path segments, so "/portal/abc" is client-facing but
 * "/portalx" is not, and "/client-onboarding" matches with or without a
 * trailing path.
 */
export function isClientFacingPath(pathname) {
  const p = typeof pathname === "string" ? pathname : "";
  return CLIENT_FACING_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith("/")) {
      return p.startsWith(prefix);
    }
    return p === prefix || p.startsWith(`${prefix}/`);
  });
}

/** Convenience for non-React code: is the browser on a client page now? */
export function isOnClientFacingPage() {
  try {
    return typeof window !== "undefined" && window.location
      ? isClientFacingPath(window.location.pathname || "")
      : false;
  } catch {
    return false;
  }
}

/** Event the interceptor fires instead of redirecting on a client page. */
export const CLIENT_LINK_EXPIRED_EVENT = "caseminder:client-link-expired";
